<script setup lang="ts" generic="T extends { id?: string }">
import { DnDOperations } from "@vue-dnd-kit/core";

const props = defineProps<{
  items: T[];
  group: string;
}>();

const emits = defineEmits<{
  (e: "reorder", items: T[]): void;
}>();

const handleDrop = (fromIndex: number, toIndex: number) => {
  const reordered = [...props.items];
  // DnDOperations.move splice-retire puis splice-insère sans tenir compte du décalage
  // d'index que ce retrait provoque ; on le compense ici puisque la sémantique de nos
  // drop zones (une avant chaque item + une finale) est « insère à la position N ».
  const insertAt = fromIndex < toIndex ? toIndex - 1 : toIndex;
  DnDOperations.move(reordered, fromIndex, reordered, insertAt);
  emits("reorder", reordered);
};

defineSlots<{
  default(props: { item: T; index: number }): unknown;
}>();
</script>

<script lang="ts">
import { computed, defineComponent, h } from "vue";
import {
  useDnDStore,
  useDraggable as useDrag,
  useDroppable as useDrop,
} from "@vue-dnd-kit/core";

// Poignée de drag pour une ligne — mêmes primitives que SettingsSortedListInput.vue.
export const DragHandle = defineComponent({
  props: {
    index: { type: Number, required: true },
    group: { type: String, required: true },
  },
  setup(props) {
    const { elementRef, isDragging, handleDragStart } = useDrag({
      groups: [props.group],
      data: computed(() => ({ index: props.index })),
    });
    return { elementRef, isDragging, handleDragStart };
  },
  render() {
    return h(
      "div",
      {
        ref: "elementRef",
        class: [
          "dr-drag-handle",
          { "dr-drag-handle-dragging": this.isDragging },
        ],
        style: { cursor: "grab", userSelect: "none" },
        onPointerdown: this.handleDragStart,
      },
      "⠿"
    );
  },
});

const DropZone = defineComponent({
  props: {
    at: { type: Number, required: true },
    group: { type: String, required: true },
  },
  emits: ["drop"],
  setup(props, { emit }) {
    // isOvered vaut `hovered.zone === elementRef` sans vérifier qu'un drag est en
    // cours ; au tout premier rendu les deux valent encore null, ce qui active
    // toutes les DropZone à la fois (flash bleu). On combine avec isDragging du
    // store global pour n'activer le surlignage que pendant un vrai drag.
    const { isDragging } = useDnDStore();
    const { elementRef, isOvered } = useDrop({
      groups: [props.group],
      data: computed(() => ({ index: props.at })),
      events: {
        onDrop: async (_store: unknown, payload: any) => {
          const dragged = payload.items?.[0]?.data;
          if (!dragged) return false;
          emit("drop", dragged.index, props.at);
          return true;
        },
      },
    });
    return { elementRef, isOvered, isDragging };
  },
  render() {
    return h("div", {
      ref: "elementRef",
      class: [
        "dr-drop-zone",
        { "dr-drop-zone-active": this.isOvered && this.isDragging },
      ],
    });
  },
});

export default defineComponent({
  components: { DropZone },
});
</script>

<template>
  <div>
    <DropZone :at="0" :group="group" @drop="handleDrop" />
    <template v-for="(item, index) in items" :key="item.id ?? index">
      <div class="d-flex align-items-center gap-2 dr-row">
        <DragHandle :index="index" :group="group" />
        <div class="flex-grow-1 min-w-0">
          <slot :item="item" :index="index" />
        </div>
      </div>
      <DropZone :at="index + 1" :group="group" @drop="handleDrop" />
    </template>
  </div>
</template>

<style lang="scss" scoped>
.dr-drag-handle {
  cursor: grab;
  user-select: none;
  letter-spacing: -0.5px;
  opacity: 0.5;
  padding: 0 4px;
  font-size: 14px;
}

.dr-drag-handle-dragging {
  opacity: 0.3;
}

// Évite le coût de layout/paint des lignes hors écran dans les longues listes ;
// contain-intrinsic-size réserve une hauteur approximative tant que la ligne
// n'a pas encore été mesurée, pour ne pas faire sauter le scroll.
.dr-row {
  content-visibility: auto;
  contain-intrinsic-size: auto 44px;
}

.dr-drop-zone {
  height: 4px;
  border-radius: 4px;
  transition: background-color 0.15s ease;
}

.dr-drop-zone-active {
  background-color: var(--bs-primary);
  height: 4px;
}
</style>
